import { useState, useEffect, useMemo } from 'react';
import { useQuery } from 'react-query';
import dayjs from 'dayjs';
import { API, graphqlOperation } from 'aws-amplify';
import { useLocation, useParams } from 'react-router-dom';
import {
  ChartLabels,
  DeviceTypes,
  StatTypes,
  ViewMode,
  TabValues,
  StatKeys,
  ModuleStatKeys,
  RunState,
  RunStateLabel,
} from '../constants';
import { API_ENDPOINT } from 'helper';
import { useDeviceStats } from 'components/DeviceDetails/service';

const customColors = {
  purple: '#5352ED',
  red: '#E74C3C',
  green: '#3CAE71',
  blue: '#00ADEE',
  orange: '#FF7C43',
  fuchsia: '#f202de',
};

const { purple, red, green, blue, orange, fuchsia } = customColors;

export const getChartColor = (chartLabel) => {
  const { Master_SOC, Current, Voltage, Power, MinTemp, MaxTemp } =
    ChartLabels;
  const lineColors = {
    [Master_SOC]: purple,
    [Power]: green,
    [MaxTemp]: red,
    [MinTemp]: blue,
    [Current]: orange,
    [Voltage]: fuchsia,
  };
  return lineColors[chartLabel];
};

export const simpleDateFormat = (isoDate) => {
  return dayjs(isoDate).format('MMMM D, h:mm A');
};

export const tickFormatter = (isoDate, viewMode) => {
  //using dayjs we want to show hours minutes and seconds if its a day view
  //but for week we want to show the date and hour
  //and for month we want to
  if (viewMode === ViewMode.DAY) {
    return dayjs(isoDate).format('h:mm A');
  }
  return dayjs(isoDate).format('MMM D, h A');
};

export const calculateBrushIndexes = (stats, viewMode = ViewMode.DAY) => {
  if (!stats || stats.length === 0) {
    // Return default indices if stats is empty or not provided
    return { startIndex: 0, endIndex: 0 };
  }

  const lastStatIndex = stats.length - 1;
  const startIndex = 0;

  const endIndex = lastStatIndex;
  return { startIndex, endIndex };
};

export const verifySingleStack = (device) => {
  // Based on how the route and HGBreadcrumb is set up,
  // there is no BMS_ID in the URL for single stack,
  // so we extract it from the device Config Data
  const isSingleStack = device?.Config?.data?.MASTER_SNS?.length < 2;
  const SINGLE_BMS_ID = device?.Config?.data?.MASTER_SNS?.[0]?.MASTER_SN;

  return {
    isSingleStack,
    SINGLE_BMS_ID: isSingleStack ? SINGLE_BMS_ID : null,
  };
};

// TODO: Refactor this hook,
// it is confusing having to get moduleID from 'BMS_ID' key for single stacks to appease how the HGBreadcrumb is set up

export const useGetIDsFromURL = () => {
  const { pathname } = useLocation();
  const pathnameArray = pathname.split('/');
  const baseRoute = pathnameArray[1];
  const { id, BMS_ID, Module_ID, UpdateID } = useParams();
  // if the path contains '_' then it indicates that the id is for device
  // if the path length is ge 25 then it indicates that the id is an uuid
  const addEdit = pathnameArray.find(
    (path) =>
      (!path.includes('_') || path.length < 25) &&
      (path.includes('add-') || path.includes('edit-'))
  );
  const history = pathnameArray.find((path) => path.includes('history'));
  const logs = pathnameArray.find((path) => path.includes('logs'));

  const IDs = useMemo(
    () => ({
      pathname,
      baseRoute,
      ...(baseRoute === 'devices'
        ? {
          Thing_ID: id,
          BMS_ID,
          Module_ID, // maybe Module_ID = BMS_ID for single stacks?
          history,
          UpdateID,
          logs,
        }
        : { Site_ID: id }),
      addEdit,
    }),
    [
      pathname,
      baseRoute,
      id,
      BMS_ID,
      Module_ID,
      history,
      UpdateID,
      addEdit,
      logs,
    ]
  );

  return IDs;
};

export const getDeviceType = (device, BMS_ID = null, MODULE_ID = null) => {
  const NEP = device?.ThingType?.toLowerCase().includes('nep');
  const EMS = device?.ThingType?.toLowerCase().includes('battery');
  const { isSingleStack } = verifySingleStack(device);

  if (NEP) {
    return DeviceTypes.NEP;
  }

  if (EMS) {
    if (MODULE_ID || (isSingleStack && BMS_ID)) return DeviceTypes.MODULE;
    if (!MODULE_ID && (isSingleStack || BMS_ID)) return DeviceTypes.BMS;
    return DeviceTypes.EMS;
  }

  // Fallback for when device doesn't have ThingType (e.g., selectedDevice is a PACK object)
  // but we can determine the type from URL params
  if (MODULE_ID || BMS_ID) {
    return DeviceTypes.MODULE;
  }

  return DeviceTypes.DEFAULT;
};

export const transformData = (stats, key = 'power_w') => {
  const consolidatedData = {};
  const map = new Map();
  Object.keys(stats).forEach((deviceSN) => {
    const uniqueMinutes = new Set();
    stats[deviceSN].forEach((stat) => {
      const isoDate = stat.isodate_utc;
      const minuteKey = isoDate.slice(0, 16);
      if (!uniqueMinutes.has(minuteKey)) {
        uniqueMinutes.add(minuteKey);
      }
      if (!consolidatedData[minuteKey]) {
        consolidatedData[minuteKey] = { isodate_utc: isoDate };
      }
      const hashKey = deviceSN + minuteKey + '';
      let avg = stat[key];
      if (key === StatKeys.Power) {
        stat.M_power = stat?.M_tv * stat?.M_cur; // add power value for BMS
        avg = stat.M_power;
      }

      if (map.has(hashKey)) {
        avg = (map.get(hashKey) + avg) / 2;
      }

      if (!isNaN(avg)) {
        map.set(hashKey, avg);
        consolidatedData[minuteKey][deviceSN] = parseFloat(avg);
      }
    });
  });

  // Convert the object into an array
  return Object.values(consolidatedData);
};

// Must transform stats different for min and max temp as the chart needs two lines
// So the keys in the final array of objects are 'Min Temp' and 'Max Temp' instead of the serial numbers
export const transformStatsDataIntoMinuteDataByKey = (stats) => {
  const consolidatedData = {};
  const map = new Map();

  //if stats is null return empty object
  if (!stats) return {};

  Object.keys(stats).forEach((deviceSN) => {
    const uniqueMinutes = new Set();
    stats[deviceSN].forEach((stat) => {
      const isoDate = stat.isodate_utc;
      const minuteKey = isoDate.slice(0, 16);
      if (!uniqueMinutes.has(minuteKey)) {
        uniqueMinutes.add(minuteKey);
        if (!consolidatedData[minuteKey]) {
          consolidatedData[minuteKey] = { isodate_utc: isoDate };
        }

        const deviceStatKeys =
          stat.device_type === StatTypes.PACK
            ? ModuleStatKeys
            : StatKeys;

        Object.entries(deviceStatKeys).forEach(([key, statKey]) => {
          // Skip if the current statKey is the date
          if (statKey === deviceStatKeys.DATE_UTC) return;

          let currentValue;

          // Custom handling for Power - multiply Current and Voltage
          if (statKey === deviceStatKeys.Power) {
            currentValue =
              stat[deviceStatKeys.Current] *
              stat[deviceStatKeys.Voltage]; // divide by 1000 to convert to kW
          } else {
            currentValue = stat[statKey];
          }

          const mapKey = minuteKey + key; // Unique key for each stat and minute

          if (!isNaN(currentValue)) {
            let newValue = currentValue;

            // If a previous value exists, calculate the new average
            if (map.has(mapKey)) {
              newValue = (map.get(mapKey) + currentValue) / 2;
            }
            map.set(mapKey, newValue);

            //if its power then use 4 decimal places
            if (statKey === deviceStatKeys.Power) {
              newValue = parseFloat(newValue).toFixed(0);
              consolidatedData[minuteKey][
                ChartLabels[key] || key
              ] = newValue;
              consolidatedData[minuteKey][
                ChartLabels.ChargingStatus
              ] = newValue > 0 ? 'Power in' : 'Power out';
            } else if (
              statKey === deviceStatKeys.MinTemp ||
              statKey === deviceStatKeys.MaxTemp
            ) {
              consolidatedData[minuteKey][
                ChartLabels[key] || key
              ] = parseFloat(newValue).toFixed(0);
            } else {
              consolidatedData[minuteKey][
                ChartLabels[key] || key
              ] = parseFloat(newValue).toFixed(2);
            }
          }
        });
      }
    });
  });

  return Object.values(consolidatedData);
};

export const fillMissingData = (data, deviceSNs) => {
  if (data.length === 0) return data;

  const filledData = [];
  const firstDate = new Date(data[0].isodate_utc);
  const lastDate = new Date(data[data.length - 1].isodate_utc);

  for (
    let date = firstDate;
    date <= lastDate;
    date.setMinutes(date.getMinutes() + 1)
  ) {
    const isoDate = date.toISOString();
    const minuteKey = isoDate.slice(0, 16); // YYYY-MM-DDTHH:mm
    const existingData = data.find((d) =>
      d.isodate_utc.startsWith(minuteKey)
    );

    const dataEntry = { isodate_utc: isoDate };
    deviceSNs.forEach((sn) => {
      dataEntry[sn] = existingData ? existingData[sn] || 0 : 0;
    });

    filledData.push(dataEntry);
  }

  return filledData;
};

export const transformMonthBatteryStats = (stats, sn) => {
  const consolidatedData = {};
  if (!stats[sn]) {
    return [];
  }
  stats[sn].forEach((stat) => {
    const isoDate = stat.isodate_utc;
    if (!consolidatedData[isoDate]) {
      consolidatedData[isoDate] = { isodate_utc: isoDate };
    }
    consolidatedData[isoDate][sn] = Math.ceil(stat.M_soc);
  });

  const sortedData = Object.values(consolidatedData).sort((a, b) => {
    return new Date(a.isodate_utc) - new Date(b.isodate_utc);
  });
  return sortedData;
};

export const transformMonthStats = (stats) => {
  const consolidatedData = {};

  Object.keys(stats).forEach((deviceSN) => {
    stats[deviceSN].forEach((stat) => {
      const isoDate = stat.isodate_utc;
      if (!consolidatedData[isoDate]) {
        consolidatedData[isoDate] = { isodate_utc: isoDate };
      }

      consolidatedData[isoDate][deviceSN] = Math.ceil(stat.power_w);
    });
  });

  // Fill missing data with 0
  const allDeviceSNs = Object.keys(stats);
  Object.values(consolidatedData).forEach((entry) => {
    allDeviceSNs.forEach((deviceSN) => {
      if (!(deviceSN in entry)) {
        entry[deviceSN] = 0;
      }
    });
  });
  const sortedData = Object.values(consolidatedData).sort((a, b) => {
    return new Date(a.isodate_utc) - new Date(b.isodate_utc);
  });
  return sortedData;
};

export const getDayTimestamps = (date) => {
  // Start and end of the day in local time for the given date
  const startOfDay = dayjs(date).startOf('day');
  const endOfDay = dayjs(date).endOf('day');

  return {
    startTimestamp: startOfDay.valueOf(), // in milliseconds
    endTimestamp: endOfDay.valueOf(), // in milliseconds
  };
};

export const getStartEndOfMonthTimestamps = (date) => {
  const startOfMonth = dayjs(date).startOf('month');
  const endOfMonth = dayjs(date).endOf('month');

  return {
    startTimestamp: startOfMonth.valueOf(),
    endTimestamp: endOfMonth.valueOf(),
  };
};

//@todo remove duplicate code of queryAdapter
export const queryAdapter = async (
  graphqlQuery,
  queryParams,
  cache = false,
  jwtToken = null
) => {
  const queryString = JSON.stringify(queryParams) + graphqlQuery;
  let response = null;
  if (cache && sessionStorage.getItem(queryString)) {
    response = sessionStorage.getItem(queryString);
    response = JSON.parse(response);
  } else {
    response = await API.graphql(
      graphqlOperation(graphqlQuery, queryParams, jwtToken)
    );
    sessionStorage.setItem(queryString, JSON.stringify(response));
  }
  return response;
};

export const useGetCachedData = (graphqlQuery, queryParams) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getCachedData = async () => {
      if (!queryParams || !graphqlQuery) return;
      try {
        const response = await queryAdapter(
          graphqlQuery,
          queryParams,
          true
        );
        if (response) setData(response.data);
      } catch (error) {
        console.error('Error fetching cached data: ', error);
      }
      setLoading(false);
    };

    getCachedData();
    return () => {
      setData(null);
      setLoading(true);
    };
  }, [queryParams, graphqlQuery]);

  return { data, loading };
};

export const getChargingState = (current) => {
  if (current > 0) {
    return RunStateLabel[RunState.Charging];
  } else if (current < 0) {
    return RunStateLabel[RunState.Discharging];
  } else {
    return RunStateLabel[RunState.Idle];
  }
};

export const getDateFromTimestamp = (timestamp) => {
  return dayjs(timestamp).format('YYYY-MM-DD');
};

export const transformChartPoints = (input) => {
  // Mapping for the key transformation
  const keyMap = {
    SOC: 'State of Charge',
    Current: 'Current',
    Voltage: 'Voltage',
    Power: 'Power',
    MinTemp: 'Min Temp',
    MaxTemp: 'Max Temp',
  };

  // Convert timestamp to ISO string
  //bug fix 9-27 for new data coming from ETL
  const isoDate = new Date(parseInt(input.ts)).toISOString();

  // Create a new object with updated keys
  const transformedData = {};

  for (const key in input) {
    if (keyMap[key]) {
      transformedData[keyMap[key]] = input[key];
    }
  }

  // Add the ISO date entry
  transformedData['isodate_utc'] = isoDate;

  return transformedData;
};

// Densifies the sparse chart stats array so that every bucket in the selected
// week/month/year range has an entry. Missing buckets get { isodate_utc } with
// no metric keys, so Recharts renders a real gap rather than collapsing the
// x-axis. Day view is untouched — it has its own minute-level fill pipeline.
export const fillMissingBuckets = (stats, startTimestamp, endTimestamp, viewMode) => {
  if (viewMode === ViewMode.DAY) return stats;

  const TOOLTIP_ANCHOR = '__tooltipAnchor';
  const unit = viewMode === ViewMode.YEAR ? 'month' : 'day';
  const existingByBucket = new Map();
  (stats || []).forEach((point) => {
    const bucketKey = dayjs(point.isodate_utc).startOf(unit).valueOf();
    existingByBucket.set(bucketKey, point);
  });

  const result = [];
  let cursor = dayjs(startTimestamp).startOf(unit);
  const end = dayjs(endTimestamp);
  while (cursor.valueOf() <= end.valueOf()) {
    const bucketKey = cursor.valueOf();
    const existing = existingByBucket.get(bucketKey);
    if (existing) {
      result.push({ ...existing, [TOOLTIP_ANCHOR]: 0 });
    } else {
      result.push({
        isodate_utc: cursor.toISOString(),
        __missing: true,
        [TOOLTIP_ANCHOR]: 0,
      });
    }
    cursor = cursor.add(1, unit);
  }
  return result;
};

// Collapses contiguous runs of __missing buckets into { x1, x2 } pairs so the
// chart can shade them with <ReferenceArea>. Each range is extended by one
// bucket on either side (to the neighboring real data point when available)
// so the shaded band visually connects to the surrounding data instead of
// leaving a sliver of unshaded space between the last point and the band.
export const getMissingRanges = (densifiedStats) => {
  const stats = densifiedStats || [];
  const ranges = [];
  let runStartIdx = null;
  let runEndIdx = null;
  stats.forEach((point, idx) => {
    if (point.__missing) {
      if (runStartIdx === null) runStartIdx = idx;
      runEndIdx = idx;
    } else if (runStartIdx !== null) {
      const x1 = stats[Math.max(0, runStartIdx - 1)].isodate_utc;
      const x2 = stats[Math.min(stats.length - 1, runEndIdx + 1)].isodate_utc;
      ranges.push({ x1, x2 });
      runStartIdx = null;
      runEndIdx = null;
    }
  });
  if (runStartIdx !== null) {
    const x1 = stats[Math.max(0, runStartIdx - 1)].isodate_utc;
    const x2 = stats[Math.min(stats.length - 1, runEndIdx + 1)].isodate_utc;
    ranges.push({ x1, x2 });
  }
  return ranges;
};

export const getTimestamps = (date, viewMode = ViewMode.DAY) => {
  // Week mode uses a retroactive 7-day range ending on the selected date,
  // matching the WeekPicker's visual model. Using startOf/endOf('week') would
  // bucket all days in the same Sun–Sat calendar week to identical timestamps,
  // preventing a chart refresh when selecting a different day in the same week.
  if (viewMode === ViewMode.WEEK) {
    return {
      startTimestamp: dayjs(date).subtract(6, 'day').startOf('day').valueOf(),
      endTimestamp: dayjs(date).endOf('day').valueOf(),
    };
  }

  const startOfMonth = dayjs(date).startOf(viewMode);
  const endOfMonth = dayjs(date).endOf(viewMode);

  return {
    startTimestamp: startOfMonth.valueOf(),
    endTimestamp: endOfMonth.valueOf(),
  };
};

export const getModuleCellString = (data) => {
  const newData = splitToBytesAndConvert(data);
  return `Module ${newData[0]} Cell ${newData[1]}`;
};

export const splitToBytesAndConvert = (decimalNumber) => {
  // Mask the lower 8 bits and shift right to get the upper byte
  let upperByte = (decimalNumber & 0xff00) >> 8;
  // Mask the lower 8 bits to get the lower byte
  let lowerByte = decimalNumber & 0x00ff;

  return [upperByte, lowerByte];
};

export const useValidateDevices = (Thing_ID, devices) => {
  const { data: stats } = useDeviceStats(Thing_ID);
  const validateDevice = (index) => {
    if (!devices || devices.length === 0) return devices;
    const verifiedSerialNumbers = Object.keys(stats);
    const newDevices = [...devices];
    const currentDevice = newDevices[index];
    if (currentDevice.status) return newDevices;
    currentDevice.status = verifiedSerialNumbers.includes(
      currentDevice.serialNumber
    )
      ? 'online'
      : 'offline';
    return newDevices;
  };
  return validateDevice;
};

export const getMonthlyStats = (statsByDeviceSN, key = 'power_w') => {
  const monthlyStatsBySN = Object.fromEntries(
    Object.entries(statsByDeviceSN).map(([deviceSN, deviceDataArray]) => {
      if (deviceDataArray.length === 0) return [deviceSN, []];
      const hourlyStats = calculateHourlyAverage(deviceDataArray, key);
      const dailyAverage = calculate6HourAverage(hourlyStats, key);

      return [deviceSN, dailyAverage];
    })
  );
  return monthlyStatsBySN;
};

const calculate6HourAverage = (hourlyData, key) => {
  const dailyData = hourlyData.reduce((acc, currentStat) => {
    const quarterHour = currentStat.hour % 6 === 0;
    let newData = { ...currentStat };

    if (quarterHour) {
      newData.sumOfAverage = currentStat[key] || 0; // Ensure initial value is 0 if undefined or null
      acc.push(newData);
    } else if (acc.length > 0) {
      // Ensure that sumOfAverage is incremented by 0 if currentStat[`averageHourly${key}`] is undefined or null
      acc[acc.length - 1].sumOfAverage += currentStat[key] || 0;
    }

    return acc;
  }, []);

  dailyData.forEach((item) => {
    if (item.sumOfAverage === 0) {
      item[key] = 0; // Set the average to 0 if sumOfAverage is 0 to avoid division by zero
    } else {
      item[key] = item.sumOfAverage / 6; // Use the dynamic key for the final average
    }
    delete item.sumOfAverage;
  });

  return dailyData;
};

const calculateHourlyAverage = (data, key) => {
  const hourlyData = data.reduce((acc, currentStat) => {
    const date = new Date(currentStat.isodate_utc);
    const day = date.getUTCDate();
    const hour = date.getUTCHours();
    let dataByDayHour = acc.find(
      (item) => item.hour === hour && item.day === day
    );
    if (!dataByDayHour) {
      date.setUTCHours(hour, 0, 0, 0);
      dataByDayHour = {
        ...currentStat,
        isodate_utc: date.toISOString().replace('Z', '+00:00'),
        day,
        hour,
        sumOfKey: 0,
        hourlyCount: 0,
      };
      acc.push(dataByDayHour);
    }
    dataByDayHour.sumOfKey += currentStat[key]; // Use the dynamic key
    dataByDayHour.hourlyCount++;
    return acc;
  }, []);

  hourlyData.forEach((item) => {
    item[key] = item.sumOfKey / item.hourlyCount;
    delete item.sumOfKey;
    delete item.hourlyCount;
  });
  const hourlyDataWithMissingHours = fillMissingDaysAndHours(hourlyData, key);
  const sorted = hourlyDataWithMissingHours.sort(
    (a, b) => new Date(a.isodate_utc) - new Date(b.isodate_utc)
  );

  return sorted;
};

const fillMissingDaysAndHours = (hourlyData, key) => {
  const firstDate = new Date(hourlyData[0]?.isodate_utc);
  const year = firstDate.getUTCFullYear();
  const month = firstDate.getUTCMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  Array.from({ length: daysInMonth }, (_, day) =>
    Array.from({ length: 24 }, (_, hour) => {
      const existingData = hourlyData.find(
        (item) => item.day === day + 1 && item.hour === hour
      );
      if (!existingData) {
        const newDate = new Date(year, month, day + 1, hour);
        const startOfDay = newDate.toISOString().replace('Z', '+00:00');
        hourlyData.push({
          isodate_utc: startOfDay,
          day: day + 1,
          hour,
          [key]: 0, // Use the dynamic key
        });
      }
    })
  );

  return hourlyData;
};

export const getPowerDomain = (stats) => {
  const MIN = -5000;
  const MAX = 5000;
  const powerData =
    stats
      ?.map((stat) => stat[ChartLabels.Power])
      .filter((v) => Number.isFinite(v)) || [];
  const maxPower = powerData.length ? Math.max(...powerData) : 0;
  const minPower = powerData.length ? Math.min(...powerData) : 0;
  let roundedMax = Math.ceil(maxPower / 1000) * 1000;
  let roundedMin = Math.floor(minPower / 1000) * 1000;
  // give a min value of -5000 and max value of 5000
  if (roundedMin > MIN) {
    roundedMin = MIN;
  }
  if (roundedMax < MAX) {
    roundedMax = MAX;
  }
  return [roundedMin, roundedMax];
};

export const getYaxisDomain = (stats, chartType, lineLabels) => {
  if (!stats || lineLabels.length === 0) {
    return [0, 0];
  }
  //assuming 50c max temp
  if (chartType.title === TabValues.Temp) {
    return [-10, 60]; // max 50c assumming temp
  }

  //for some reason stats there is stat  "State of Charge"
  //but the key that is coming in is "Master State of Charge"
  if (!stats || lineLabels.length === 0) return [0, 100];

  //there are two values in each stat {[serialNumber]: value, isodate_utc: value}
  const minMaxPerSn = lineLabels.map((lineLabel) => {
    if (
      chartType.title === ChartLabels.All &&
      lineLabel === ChartLabels.Power
    ) {
      return [0, 0];
    }

    // Extract all values for a given sn across all stats, ensuring they are numbers.
    // Filter out NaN/undefined from missing-day buckets so they don't poison min/max.
    const llValues = stats
      .map((stat) => Number(stat[lineLabel]))
      .filter((v) => Number.isFinite(v));
    if (llValues.length === 0) return [0, 0];
    // Calculate min and max, ensuring they are integers
    const min = Math.floor(Math.min(...llValues));
    const max = Math.ceil(Math.max(...llValues));
    return [min, max];
  });
  //get all the min values
  const minValues = minMaxPerSn.map((minMax) => minMax[0]);
  //get all the max values
  const maxValues = minMaxPerSn.map((minMax) => minMax[1]);
  //get the min of the min values
  const minValue = Math.min(...minValues);
  const min = minValue > 0 ? 0 : minValue;

  //get the max of the max values
  const max = Math.max(...maxValues);
  const orderOfMagnitude = max;
  return [min, orderOfMagnitude];
};

export const formatYAxis = (tickItem) => {
  // Remember the original sign of the tickItem
  const sign = Math.sign(tickItem);

  // Work with the absolute value for formatting
  let absTickItem = Math.abs(tickItem);

  let formattedValue = `${absTickItem}`; // Default formatted value as a string

  // Apply formatting based on the magnitude of absTickItem
  if (absTickItem < 1) {
    formattedValue = absTickItem.toFixed(2);
  } else if (absTickItem < 10) {
    formattedValue = absTickItem.toFixed(1);
  } else if (absTickItem < 1000) {
    formattedValue = absTickItem.toFixed(0);
  } else if (absTickItem < 10000) {
    formattedValue = `${(absTickItem / 1000).toFixed(0)}k`;
  } else if (absTickItem < 100000) {
    formattedValue = `${(absTickItem / 1000).toFixed(1)}k`;
  } else if (absTickItem < 1000000) {
    formattedValue = `${(absTickItem / 1000).toFixed(0)}k`;
  } else if (absTickItem < 10000000) {
    formattedValue = `${(absTickItem / 1000000).toFixed(0)}M`;
  } else if (absTickItem < 100000000) {
    formattedValue = `${(absTickItem / 1000000).toFixed(1)}M`;
  }

  // Restore the original sign if the value was negative
  if (sign === -1) {
    formattedValue = `-${formattedValue}`;
  }

  return formattedValue;
};

export const getVersionsFromDevice = (device, IDs) => {
  const EMS = device?.Config?.data;
  const { SINGLE_BMS_ID } = verifySingleStack(device);
  const BMS = device?.Config?.data?.MASTER_SNS.find(
    (master) => master.MASTER_SN === IDs?.BMS_ID || SINGLE_BMS_ID
  );
  return {
    hw_ver: BMS?.hw_ver,
    fw_ver: BMS?.fw_ver,
    E_hw_ver: EMS?.hw_ver,
    E_fw_ver: EMS?.fw_ver,
  };
};

export const checkStackAlerts = (masterSN, packs, activeAlerts) => {
  let faultCount = 0;
  let alarmCount = 0;

  if (!activeAlerts) return { hasFault: false, hasAlarm: false, faultCount, alarmCount };

  const serialNumbers = [
    masterSN,
    ...(packs || []).map((p) => (typeof p === 'string' ? p.trim() : p?.PACK_SN)),
  ].filter(Boolean);

  Object.keys(activeAlerts).forEach((key) => {
    const deviceType = key.endsWith('MASTER') ? 'MASTER' : key.endsWith('PACK') ? 'PACK' : key.endsWith('EMS') ? 'EMS' : null;
    const suffix = deviceType ?? '';
    const alertSN = key.slice(0, key.length - suffix.length);
    if (serialNumbers.includes(alertSN)) {
      Object.keys(activeAlerts[key]).forEach((alertKey) => {
        if (alertKey.startsWith('M_F') || alertKey.startsWith('F_')) {
          faultCount++;
        } else {
          alarmCount++;
        }
      });
    }
  });

  return { hasFault: faultCount > 0, hasAlarm: alarmCount > 0, faultCount, alarmCount };
};

export const aggregateAllAlerts = (activeAlerts) => {
  let faultCount = 0;
  let alarmCount = 0;

  if (!activeAlerts) return { hasFault: false, hasAlarm: false, faultCount, alarmCount };

  Object.keys(activeAlerts).forEach((key) => {
    Object.keys(activeAlerts[key]).forEach((alertKey) => {
      if (alertKey.startsWith('M_F') || alertKey.startsWith('F_')) {
        faultCount++;
      } else {
        alarmCount++;
      }
    });
  });

  return { hasFault: faultCount > 0, hasAlarm: alarmCount > 0, faultCount, alarmCount };
};

export const checkFaultsAndAlarms = (serialNumber, activeAlerts) => {
  let faultCount = 0;
  let alarmCount = 0;

  if (!activeAlerts) return { hasFault: false, hasAlarm: false, faultCount, alarmCount };

  Object.keys(activeAlerts).forEach((key) => {
    if (key.includes(serialNumber)) {
      Object.keys(activeAlerts[key]).forEach((alertKey) => {
        if (alertKey.startsWith('M_F') || alertKey.startsWith('F_')) {
          faultCount++;
        } else {
          alarmCount++;
        }
      });
    }
  });

  return { hasFault: faultCount > 0, hasAlarm: alarmCount > 0, faultCount, alarmCount };
};

// Add message mapping for fault and alarm keys
const alertMessages = {
  cellov: 'Cell Over Voltage',
  celluv: 'Cell Under Voltage',
  diffv: 'Cell Differential Voltage',
  totalov: 'Total Voltage Too High',
  totaluv: 'Total Voltage Too Low',
  chgoc: 'Maximum Charge Current Exceeded',
  chgot: 'Attempt to Charge when cell Temperature too High',
  batut: 'Cell temperature too low',
  dchgot: 'Attempt to Discharge when temperature too high',
  dchgut: 'Attempt to Discharge when temperature too low',
  envot: 'EMS Environmental Temperature too High',
  envut: 'EMS Environmental Temperature too Low',
  mosot: 'A Pack had a MOSFET Over Temperature',
  socu: 'State of Charge Low',
  dchgoc1: 'Exceeded Discharge Current 1',
  dchgoc2: 'Exceeded Discharge Current 2',
};

// Modify getFaultsAndAlarms to return messages with levels
export const getFaultsAndAlarms = (serialNumber, activeAlerts, expectedDeviceType = null) => {
  const messages = [];

  if (!activeAlerts) return messages;
  Object.keys(activeAlerts).forEach((key) => {
    const deviceType = key.endsWith('MASTER') ? 'MASTER' : key.endsWith('PACK') ? 'PACK' : key.endsWith('EMS') ? 'EMS' : null;
    const suffix = deviceType ?? '';
    const alertSerialNumber = key.slice(0, key.length - suffix.length);
    if (alertSerialNumber === serialNumber && (!expectedDeviceType || deviceType === expectedDeviceType)) {
      Object.keys(activeAlerts[key]).forEach((alertKey) => {
        // remove M_A or M_F or F_ or A_ from the alert key
        const mkey = alertKey.replace(/M_A|A_|M_F|F_/g, '');
        const message = alertMessages[mkey]; // this is what needs help
        const level =
          alertKey.startsWith('M_F') || alertKey.startsWith('F_')
            ? 'Fault'
            : 'Alarm';
        if (message) {
          messages.push({ level, message, deviceType, alertSerialNumber });
        }
      });
    }
  });

  return messages;
};

// Note: DeviceDailySummaries table is currently empty and no longer used for now.
// It is used to get the highlighted days in the calendar
// We might bring it back in the future hence the code is not removed
export const fetchDeviceDailySummaries = async ({ Thing_ID, date, signal }) => {
  try {
    const { startTimestamp, endTimestamp } =
      getStartEndOfMonthTimestamps(date);
    const url = new URL(`${API_ENDPOINT}/deviceDailySummaries`);
    url.searchParams.append('Thing_ID', Thing_ID);
    url.searchParams.append('startTimestamp', startTimestamp);
    url.searchParams.append('endTimestamp', endTimestamp);

    const res = await fetch(url.toString(), { signal });
    const { dailySummaries = [] } = await res.json();
    return dailySummaries.map((item) => {
      const dt = new Date(item.ts);
      return dt.getUTCDate();
    });
  } catch (error) {
    console.error('Error in fetchDeviceDailySummaries:', error);
    throw error;
  }
};

export const useGetHighlightedDays = (date) => {
  const { Thing_ID } = useGetIDsFromURL();
  const monthKey = useMemo(
    () => `${date.$M + 1}-${date.$y}`,
    [date.$M, date.$y]
  );

  const {
    data: highlightedDays = [],
    isLoading,
    error,
  } = useQuery(
    ['highlightedDays', Thing_ID, monthKey],
    ({ signal }) => fetchDeviceDailySummaries({ Thing_ID, date, signal }),
    {
      keepPreviousData: true,
    }
  );

  return { highlightedDays, isLoading, error };
};
